if (!customElements.get('sticky-atc')) {
  customElements.define(
    'sticky-atc',
    class StickyAtc extends HTMLElement {
      connectedCallback() {
        this.sectionId = this.dataset.section;
        this.btn = this.querySelector('.sticky-atc__btn');
        if (!this.btn) return;
        this.btnLabel = this.btn.querySelector('.sticky-atc__btn-label');
        this.btnPriceEl = this.btn.querySelector('.sticky-atc__btn-price');
        this.pickerType = this.dataset.pickerType;
        this.mainBtn = document.getElementById(`ProductSubmitButton-${this.sectionId}`);
        this.events = new AbortController();
        this.mobileQuery = matchMedia('(max-width: 749px)');
        this.motionQuery = matchMedia('(prefers-reduced-motion: reduce)');
        this.syncMotion = this.syncMotion.bind(this);
        this.mobileQuery.addEventListener('change', this.syncMotion, { signal: this.events.signal });
        this.motionQuery.addEventListener('change', this.syncMotion, { signal: this.events.signal });
        document.addEventListener('visibilitychange', this.syncMotion, { signal: this.events.signal });
        const resize = () => this.updateHeight();
        window.addEventListener('resize', resize, { signal: this.events.signal });
        window.visualViewport?.addEventListener('resize', resize, { signal: this.events.signal });
        this._resize = new ResizeObserver(() => this.updateHeight());
        this._buttonState = new MutationObserver(() => this.syncMainButton());
        if (this.mainBtn) this._buttonState.observe(this.mainBtn, {
          attributes: true, attributeFilter: ['disabled', 'aria-disabled', 'class'], childList: true, characterData: true, subtree: true,
        });
        this._ownState = new MutationObserver(this.syncMotion);
        this._ownState.observe(this.btn, {
          attributes: true, attributeFilter: ['disabled', 'aria-disabled', 'class'],
        });

        this.setupVisibility();
        this.setupPicker();
        this.syncMainButton();
        this.unsubVariant = subscribe(PUB_SUB_EVENTS.variantChange, this.onVariantChange.bind(this));
        this.btn.addEventListener('click', this.onButtonClick.bind(this), { signal: this.events.signal });
      }

      disconnectedCallback() {
        this.unsubVariant?.();
        this._io?.disconnect();
        this._resize?.disconnect();
        this._buttonState?.disconnect();
        this._ownState?.disconnect();
        this.events?.abort();
        this.classList.remove('sticky-atc--visible', 'sticky-atc--shine-active');
        this.inert = true;
        const remaining = document.querySelector('sticky-atc.sticky-atc--visible');
        if (remaining) remaining.updateHeight();
        else {
          document.body.classList.remove('has-sticky-atc');
          document.documentElement.style.removeProperty('--sticky-atc-height');
        }
      }

      updateHeight() {
        if (!this.isConnected) return;
        // At extreme text enlargement keep the main CTA accessible instead of
        // covering the viewport with a clipped fixed button. Recover when it fits.
        const fits = !this.mobileQuery.matches ||
          this.getBoundingClientRect().height <= (window.visualViewport?.height || window.innerHeight);
        const visible = this.visibleRequested && fits;
        this.classList.toggle('sticky-atc--visible', Boolean(visible));
        this.inert = !visible;
        const remaining = document.querySelector('sticky-atc.sticky-atc--visible');
        document.body.classList.toggle('has-sticky-atc', Boolean(remaining));
        if (!remaining) document.documentElement.style.removeProperty('--sticky-atc-height');
        else {
          const height = `${remaining.offsetHeight}px`;
          if (document.documentElement.style.getPropertyValue('--sticky-atc-height') !== height) {
            document.documentElement.style.setProperty('--sticky-atc-height', height);
          }
        }
        this.syncMotion();
      }

      setVisible(visible) {
        this.visibleRequested = visible;
        this._resize.disconnect();
        if (visible) this._resize.observe(this);
        this.updateHeight();
      }

      syncMainButton() {
        if (!this.dataset.scrollTarget && this.mainBtn) {
          const loading = this.mainBtn.classList.contains('loading');
          this.btn.classList.toggle('loading', loading);
          this.btn.disabled = this.mainBtn.disabled;
          // Unavailable combinations do not publish variantChange in product-info.
          // Only copy known terminal labels; preserve our custom label while fetching.
          const mainLabel = this.mainBtn.querySelector('span')?.textContent.trim();
          const strings = window.variantStrings || {};
          if (this.mainBtn.disabled && mainLabel === strings.unavailable) this.setButtonState('unavailable');
          else if (this.mainBtn.disabled && mainLabel === strings.soldOut) this.setButtonState('soldout');
          if (this.mainBtn.getAttribute('aria-disabled') === 'true') this.btn.setAttribute('aria-disabled', 'true');
          else this.btn.removeAttribute('aria-disabled');
          this.btn.querySelector('.loading__spinner')?.classList.toggle('hidden', !loading);
        }
        this.syncMotion();
      }

      syncMotion() {
        const active = this.isConnected &&
          this.querySelector('.sticky-atc__shine') && !this.motionQuery.matches &&
          !document.hidden && this.classList.contains('sticky-atc--visible') && !this.btn.disabled &&
          !this.btn.classList.contains('loading') && this.btn.getAttribute('aria-disabled') !== 'true';
        this.classList.toggle('sticky-atc--shine-active', Boolean(active));
      }

      setupVisibility() {
        if (this.dataset.displayWhen === 'always') {
          this.setVisible(true);
          return;
        }
        this.setVisible(false);
        if (!this.mainBtn) return;
        this._io = new IntersectionObserver((entries) => {
          const { isIntersecting, boundingClientRect } = entries[0];
          const visible = !isIntersecting && boundingClientRect.top < 0;
          this.setVisible(visible);
        });
        this._io.observe(this.mainBtn);
      }

      setupPicker() {
        const selects = this.querySelectorAll('.sticky-atc__select');
        if (!selects.length) return;

        if (this.pickerType === 'combined') {
          selects[0].addEventListener('change', (e) => this.pushVariantId(e.target.value), { signal: this.events.signal });
        } else {
          // Separate pickers: resolve variant from selected options
          const variants = JSON.parse(this.querySelector('.sticky-atc__variants-json')?.textContent || '[]');
          selects.forEach((select) => {
            select.addEventListener('change', () => {
              const selected = [...selects].map((s) => s.value);
              const match = variants.find((v) =>
                selected.every((val, i) => v[`option${i + 1}`] === val)
              );
              if (match) this.pushVariantId(match.id);
            }, { signal: this.events.signal });
          });
        }
      }

      // Updates the main product form's variant input and triggers Dawn's variant change flow
      pushVariantId(variantId) {
        const input = document.querySelector(`#product-form-${this.sectionId} [name="id"]`);
        if (input && input.value !== String(variantId)) {
          input.value = variantId;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }

      onVariantChange({ data: { sectionId, html, variant } }) {
        if (sectionId !== this.sectionId) return;

        // Sync price block
        const priceContainer = this.querySelector(`#sticky-atc-price-container-${this.sectionId}`);
        if (priceContainer) {
          const updated = html.querySelector(`#price-${sectionId}`);
          if (updated) priceContainer.innerHTML = updated.innerHTML;
        }

        // Sync price in button
        if (this.btnPriceEl && variant) {
          this.btnPriceEl.textContent = `${Shopify.formatMoney(variant.price, theme.moneyFormat)} •`;
        }

        // Sync product image
        const img = this.querySelector(`#sticky-atc-image-${this.sectionId}`);
        if (img && variant?.featured_image) {
          img.src = variant.featured_image.src;
          img.alt = variant.featured_image.alt || '';
        }

        // Sync pickers to match main variant
        if (variant) {
          const combined = this.querySelector('.sticky-atc__picker--combined select');
          if (combined) combined.value = variant.id;

          this.querySelectorAll('.sticky-atc__picker--separate select').forEach((sel, i) => {
            sel.value = variant[`option${i + 1}`] || '';
          });
        }

        // Sync button state
        if (!variant) {
          this.setButtonState('unavailable');
        } else if (!variant.available) {
          this.setButtonState('soldout');
        } else {
          this.setButtonState('available');
        }
      }

      setButtonState(state) {
        const strings = window.variantStrings || {};
        if (this.btnPriceEl) this.btnPriceEl.hidden = state !== 'available';
        if (state === 'available') {
          this.btn.disabled = false;
          this.btnLabel.textContent = this.dataset.btnLabel;
        } else {
          this.btn.disabled = true;
          this.btnLabel.textContent = state === 'soldout'
            ? (strings.soldOut || 'Sold out')
            : (strings.unavailable || 'Unavailable');
        }
        this.syncMotion();
      }

      onButtonClick() {
        if (this.btn.disabled || this.btn.getAttribute('aria-disabled') === 'true' || this.btn.classList.contains('loading')) return;
        const scrollTarget = this.dataset.scrollTarget;
        if (scrollTarget) {
          const el = document.querySelector(scrollTarget);
          el?.scrollIntoView({ behavior: this.motionQuery.matches ? 'instant' : 'smooth', block: 'center' });
          return;
        }
        // Delegate to main buy button — reuses all of Dawn's ProductForm + CartDrawer logic
        document.getElementById(`ProductSubmitButton-${this.sectionId}`)?.click();
      }
    }
  );
}
